import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import api from '../utils/api'
import { socketService } from '../services/socketService'
import notificationService from '../services/notificationService'

export const useChatStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    sessions: [],
    activeSession: null,
    activeSessionId: null,
    messages: {},
    isLoading: false,
    error: null,
    typingUsers: {},
    onlineUsers: new Set(),

    // Actions
    // Components derive the open session from `sessions` + activeSessionId, so status
    // changes pushed over the socket (waiting -> active -> closed) are always reflected.
    setActiveSession: (sessionId) => {
      if (!sessionId) {
        set({ activeSessionId: null, activeSession: null })
        return
      }

      const session = get().sessions.find(s => s.sessionId === sessionId)
      set({ activeSessionId: sessionId, activeSession: session || null })

      socketService.joinSession(sessionId)

      // If session is waiting, automatically take it
      if (session?.status === 'waiting') {
        socketService.takeChat(sessionId)
      }
    },

    addMessage: (sessionId, message) => {
      console.log('➕ Adding message to session:', sessionId, message);
      set(state => ({
        messages: {
          ...state.messages,
          [sessionId]: [...(state.messages[sessionId] || []), message]
        }
      }))
    },

    sendMessage: async (sessionId, content) => {
      try {
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const message = {
          sessionId,
          content,
          sender: 'agent',
          type: 'text',
          timestamp: new Date().toISOString(),
          // Add a temporary ID to track optimistic updates
          tempId,
          id: tempId, // Use tempId as id for React key consistency
          messageId: tempId // Also set messageId for backend compatibility
        }

        // Optimistically add message with temp ID
        get().addMessage(sessionId, message)

        // Send via socket
        socketService.sendMessage(message)

        return { success: true }
      } catch (error) {
        set({ error: 'Failed to send message' })
        return { success: false, error: 'Failed to send message' }
      }
    },

    closeChat: async (sessionId, reason = 'agent_ended') => {
      if (!sessionId) {
        return { success: false, error: 'Invalid session ID' }
      }

      try {
        // Close via socket
        socketService.closeChat(sessionId, reason)

        // Update local state immediately
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === sessionId
              ? { ...s, status: 'closed', endedAt: new Date(), endReason: reason }
              : s
          ),
          activeSession: state.activeSession?.sessionId === sessionId ? null : state.activeSession
        }))

        return { success: true }
      } catch (error) {
        console.error('❌ Error closing chat:', error)
        return { success: false, error: error.message }
      }
    },

    transferSession: async (sessionId, targetAgentId) => {
      if (!sessionId || !targetAgentId) {
        return { success: false, error: 'Session ID and target agent ID are required' }
      }

      try {
        // Transfer via socket
        socketService.transferSession(sessionId, targetAgentId)

        return { success: true }
      } catch (error) {
        console.error('❌ Error transferring session:', error)
        return { success: false, error: error.message }
      }
    },

    updateSessionStatus: (sessionId, status) => {
      set(state => ({
        sessions: state.sessions.map(session =>
          session.sessionId === sessionId
            ? { ...session, status }
            : session
        )
      }))
    },

    setTyping: (sessionId, userId, isTyping) => {
      set(state => ({
        typingUsers: {
          ...state.typingUsers,
          [sessionId]: {
            ...state.typingUsers[sessionId],
            [userId]: isTyping
          }
        }
      }))
    },

    addOnlineUser: (userId) => {
      set(state => ({
        onlineUsers: new Set([...state.onlineUsers, userId])
      }))
    },

    removeOnlineUser: (userId) => {
      set(state => {
        const newOnlineUsers = new Set(state.onlineUsers)
        newOnlineUsers.delete(userId)
        return { onlineUsers: newOnlineUsers }
      })
    },

    clearError: () => {
      set({ error: null })
    },

    // Goes through the axios instance so an expired access token is refreshed transparently
    fetchSessions: async () => {
      try {
        set({ isLoading: true, error: null })

        const response = await api.get('/admin/sessions', { params: { status: 'all' } })
        const fetched = response.data.sessions || []
        console.log('📊 Fetched sessions:', fetched.length)

        set({ sessions: fetched, isLoading: false })

        // Deduplicate after fetching
        get().deduplicateSessions()

        return { success: true, sessions: fetched }
      } catch (error) {
        console.error('❌ Error fetching sessions:', error)
        const message = error.response?.data?.error || error.message
        set({ error: message, isLoading: false })
        return { success: false, error: message }
      }
    },

    // Helper function to remove duplicate sessions
    deduplicateSessions: () => {
      set(state => {
        const bySessionId = new Map()
        // Later entries win, original order is kept
        for (const session of state.sessions) {
          bySessionId.set(session.sessionId, session)
        }
        const uniqueSessions = Array.from(bySessionId.values())

        if (uniqueSessions.length === state.sessions.length) return {}
        console.log(`🧹 Deduplicated sessions: ${state.sessions.length} → ${uniqueSessions.length}`)
        return { sessions: uniqueSessions }
      })
    },

    // Insert a server message, or replace the optimistic copy / earlier copy of the same message
    upsertMessage: (message) => {
      set(state => {
        const sessionMessages = state.messages[message.sessionId] || []
        const messageId = message.id || message.messageId
        const incomingTime = new Date(message.timestamp || message.createdAt).getTime()

        const index = sessionMessages.findIndex(m => {
          if (messageId && (m.id === messageId || m.messageId === messageId || m.tempId === messageId)) {
            return true
          }
          // Optimistic agent message waiting for its server confirmation. Only unconfirmed
          // (tempId) messages are matched by content, so repeated identical messages survive.
          return !!m.tempId && m.sender === message.sender && m.content === message.content &&
            Math.abs(new Date(m.timestamp || m.createdAt).getTime() - incomingTime) < 10000
        })

        const normalized = { ...message, id: messageId }

        if (index !== -1) {
          const updated = [...sessionMessages]
          updated[index] = normalized
          return { messages: { ...state.messages, [message.sessionId]: updated } }
        }

        return {
          messages: { ...state.messages, [message.sessionId]: [...sessionMessages, normalized] }
        }
      })
    },

    // Initialize chat store
    initialize: () => {
      // Clean up any existing duplicate sessions
      get().deduplicateSessions()

      // Remove any existing listeners to prevent duplicates (initialize runs per login/remount)
      ;[
        'message_received', 'message_sent_confirmation', 'typing_start', 'typing_stop',
        'session_updated', 'new_chat_request', 'agent_login_success', 'session_joined',
        'chat_taken', 'user_online', 'user_offline', 'chat_closed', 'session_timeout',
        'session_transferred', 'transfer_completed', 'error', 'connect_error',
        'ai_session_started', 'ai_activity', 'socket_connected'
      ].forEach(event => socketService.off(event))

      // After a reconnect, resync the list and rejoin the open chat
      socketService.on('socket_connected', async () => {
        await get().fetchSessions()
        const { activeSessionId, sessions } = get()
        if (!activeSessionId) return
        socketService.joinSession(activeSessionId)
        if (sessions.find(s => s.sessionId === activeSessionId)?.status === 'waiting') {
          socketService.takeChat(activeSessionId)
        }
      })

      // Set up socket event listeners
      socketService.on('message_received', (message) => {
        console.log('📨 Agent received message:', message)
        console.log('📊 Current messages for session:', get().messages[message.sessionId]?.length || 0)

        // Show notification for new message (only if from user, not agent)
        if (message.sender === 'user') {
          notificationService.notifyNewMessage(message)
        }

        get().upsertMessage(message)
      })

      // Handle message sent confirmation (for agent's own messages)
      socketService.on('message_sent_confirmation', (message) => {
        console.log('✅ Agent message sent confirmation:', message)
        // Replace the optimistic message with the confirmed server message
        get().upsertMessage(message)
      })

      socketService.on('typing_start', ({ sessionId, userId }) => {
        get().setTyping(sessionId, userId, true)
      })

      socketService.on('typing_stop', ({ sessionId, userId }) => {
        get().setTyping(sessionId, userId, false)
      })

      socketService.on('session_updated', (session) => {
        set(state => {
          const exists = state.sessions.some(s => s.sessionId === session.sessionId)
          return {
            sessions: exists
              ? state.sessions.map(s => s.sessionId === session.sessionId ? { ...s, ...session } : s)
              : [session, ...state.sessions]
          }
        })
      })

      socketService.on('new_chat_request', (data) => {
        console.log('🆕 New chat request received:', data)
        console.log('📊 Current sessions before update:', get().sessions.length)

        // Show notification for new chat request
        notificationService.notifyNewChat(data)

        // Check if session already exists to prevent duplicates
        set(state => {
          const existingSessionIndex = state.sessions.findIndex(s => s.sessionId === data.sessionId)

          if (existingSessionIndex !== -1) {
            // Update existing session with latest data
            console.log('📝 Updating existing session:', data.sessionId)
            const updatedSessions = [...state.sessions]
            updatedSessions[existingSessionIndex] = {
              ...updatedSessions[existingSessionIndex],
              messageCount: data.messageCount,
              lastMessage: data.lastMessage,
              startedAt: data.timestamp,
              status: 'waiting',
              mode: 'agent'
            }
            return { sessions: updatedSessions }
          } else {
            // Add new session to the list
            console.log('➕ Adding new session:', data.sessionId)
            return {
              sessions: [...state.sessions, {
                sessionId: data.sessionId,
                userId: data.userId,
                userInfo: data.userInfo,
                status: 'waiting',
                mode: 'agent',
                messageCount: data.messageCount,
                lastMessage: data.lastMessage,
                startedAt: data.timestamp,
                lastActivity: data.timestamp
              }]
            }
          }
        })

        // Also refresh sessions from server to ensure consistency
        setTimeout(() => {
          console.log('🔄 Refreshing sessions after new chat request')
          get().fetchSessions()
        }, 1000)
      })

      socketService.on('agent_login_success', (data) => {
        console.log('✅ Agent login successful:', data)
      })

      // Make sure the session the agent opened exists in the list (e.g. opened from an email link)
      const mergeSession = (state, data) => {
        const info = {
          sessionId: data.sessionId,
          userId: data.userId,
          userInfo: data.userInfo,
          mode: data.mode,
          status: data.status,
          assignedAgent: data.assignedAgent
        }
        const exists = state.sessions.some(s => s.sessionId === data.sessionId)
        return exists
          ? state.sessions.map(s => s.sessionId === data.sessionId ? { ...s, ...info } : s)
          : [{ ...info, lastActivity: new Date().toISOString() }, ...state.sessions]
      }

      socketService.on('session_joined', (data) => {
        console.log('🔗 Agent joined session:', data)
        set(state => ({
          sessions: mergeSession(state, data),
          messages: {
            ...state.messages,
            [data.sessionId]: data.messages || []
          }
        }))
      })

      socketService.on('chat_taken', (data) => {
        console.log('✋ Chat taken:', data)
        set(state => ({
          sessions: mergeSession(state, { ...data, status: 'active' }),
          messages: {
            ...state.messages,
            [data.sessionId]: data.messages || []
          }
        }))
      })

      socketService.on('user_online', (userId) => {
        get().addOnlineUser(userId)
      })

      socketService.on('user_offline', (userId) => {
        get().removeOnlineUser(userId)
      })

      // Handle chat closed event
      socketService.on('chat_closed', (data) => {
        console.log('🔒 Chat closed:', data)
        // Remove the session from active sessions and update status
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === data.sessionId
              ? { ...s, status: 'closed', endedAt: data.timestamp, endReason: data.reason }
              : s
          )
        }))

        // Clear messages for the closed session after a delay to allow agents to see the closure
        setTimeout(() => {
          set(state => {
            const { [data.sessionId]: removedMessages, ...remainingMessages } = state.messages
            return { messages: remainingMessages }
          })
        }, 5000) // Keep messages for 5 seconds to show closure
      })

      // Handle session timeout
      socketService.on('session_timeout', (data) => {
        console.log('⏰ Session timeout:', data)
        // Similar to chat_closed but with timeout reason
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === data.sessionId
              ? { ...s, status: 'timeout', endedAt: new Date(), endReason: 'timeout' }
              : s
          )
        }))
      })

      // Handle socket errors
      socketService.on('error', (error) => {
        console.error('❌ Socket error:', error)
        // You can add error handling UI updates here if needed
      })

      // Handle connection errors
      socketService.on('connect_error', (error) => {
        console.error('❌ Connection error:', error)
        // You can add connection error handling UI updates here if needed
      })

      // Handle AI session started
      socketService.on('ai_session_started', (data) => {
        console.log('🤖 AI session started:', data)
        // Add the AI session to the sessions list
        const newSession = {
          sessionId: data.sessionId,
          userId: data.userId,
          userInfo: data.userInfo,
          status: data.status,
          mode: data.mode,
          startedAt: data.timestamp,
          lastActivity: data.timestamp,
          messageCount: data.messageCount
        }

        set(state => ({
          sessions: [newSession, ...state.sessions.filter(s => s.sessionId !== data.sessionId)]
        }))
      })

      // Handle AI activity
      socketService.on('ai_activity', (data) => {
        console.log('🤖 AI activity:', data)
        // Update session activity and add message (an agent inside the room also gets message_received)
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === data.sessionId
              ? { ...s, lastActivity: data.timestamp }
              : s
          )
        }))
        if (data.message) {
          get().upsertMessage({ ...data.message, sessionId: data.message.sessionId || data.sessionId })
        }
      })

      // Handle session transferred (when another agent transfers a session to this agent)
      socketService.on('session_transferred', (data) => {
        console.log('🔄 Session transferred to this agent:', data)
        
        // Show notification about the transfer
        notificationService.notifySessionTransferred(data)
        
        // Update session with new assignment
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === data.sessionId
              ? { ...s, assignedAgent: data.session.assignedAgent, status: 'active' }
              : s
          )
        }))
        
        // Auto-join the transferred session
        socketService.joinSession(data.sessionId)
      })

      // Handle transfer completed (confirmation when this agent transfers a session)
      socketService.on('transfer_completed', (data) => {
        console.log('✅ Transfer completed:', data)
        
        // Show success notification
        notificationService.notifyTransferCompleted(data)
        
        // Update session status - remove from this agent's active sessions
        set(state => ({
          sessions: state.sessions.map(s =>
            s.sessionId === data.sessionId
              ? { ...s, status: 'transferred', transferredTo: data.targetAgent }
              : s
          ),
          // Clear active session if it was the transferred one
          activeSession: state.activeSession?.sessionId === data.sessionId ? null : state.activeSession
        }))
      })
    }
  }))
)
